import React from "react";

export const BlackMagicTomeIContent: React.FC = () => (
    <>
        <p className="tome-attribution">
            <em>by Neth, wizard</em>
        </p>

        <p>
            Any decent book would start by describing the origins of the subject
            it is trying to deal with. As far as black magic is concerned, it is
            impossible for me to do such a thing. This art — as I would never
            call it magic, but art — is older than any Dragon or Mountain. Some
            say it came with the world, some say the world itself needed black
            magic for its creation.
        </p>
        <p>
            As for the name, "Black Magic," as I said before I do not consider
            it as magic, much more as an art. It does require one to incant and
            utter magic words, but, like any other form of magic we know, it
            does not use any reagent or spell energy of any sort. The "magic" as
            we know it is a gift, a trainable gift. As you develop your body,
            you will be able to generate more energy, and to transform it into
            the magic you want to cast. Black Magic is very different. Unlike
            the magics we know, it does not require the "caster" to produce any
            energy.
        </p>
        <p>
            You will be surprised to learn that Black Magic, in its most pure
            form, is everywhere. Every tree, every building, every single human
            being is constantly surrounded by that energy. Every breath that you
            take contains some of it. In this form, however, it is totally
            harmless and unable to affect us in any way. Only those who are
            skilled in the Art of manipulating this energy can actually create
            anything.
        </p>
        <p>
            How do these people manipulate this energy? Is this an Art we can
            actually learn, or is it just a gift from above? Wise is the one who
            could answer this question. I myself am able to manipulate a bit of
            this energy, but I will tell you about this later.
        </p>
        <p>
            The main purpose of manipulating this energy is to open gates. The
            first is the gate to your own body. Before being able to do anything
            with Black Magic, you must let it flow inside your body, fill it
            entirely, take over all your senses, and try to control you. In
            fact, the more Black Magic you let inside you, the more easily you
            can fall under its control. If that ever happens, it will only take
            a few seconds for Black Magic to consume any form of life inside
            you, leaving a babbling emotionless shell of a man where you once
            stood. I have heard of some of these "consumed" individuals roaming
            the lands, killing and pillaging those who were once their own
            families, before falling to the ground and dying, their hearts
            totally consumed by the energy they once tried to manipulate...
        </p>
        <p>
            It is also said that once your body was touched by this energy,
            never again will it be the same. The most obvious change would be
            dark spots appearing all over the magician's hands. The more Black
            Magic is used, the bigger those spots get, to the point of covering
            the whole hands, and then starting to spread to the arms. I have
            never heard of anyone using Black Magic so much that those spots
            started covering their torso or heart, but I can only imagine what
            would happen if that was the case. Again, I will tell more about all
            the side effects later.
        </p>
        <p>
            As I said before, the main purpose of manipulating Black Magic is to
            open gates. It is mainly used to open a gate between two beings, and
            to allow instant transportation from one to another, without even
            requiring both of them to be able to manipulate it. I have tried to
            gather and understand the magic words, from my personal experience
            and witnesses, and here is what I have so far:
        </p>

        <div className="tome-spell-list">
            <div className="tome-spell-entry">
                <span className="tome-spell-word">✦ Mora ✦</span>
                <p>
                    The main word. It is used to open the Gate of your own body
                    and to let Black Magic flow inside it. All Black Magic
                    "spells" begin with this incantation. Depending on the power
                    of the spell one is trying to cast, they may need to utter
                    this word more than once, in order to allow more energy to
                    flow into their body.
                </p>
            </div>
            <div className="tome-spell-entry">
                <span className="tome-spell-word">✦ Olsa ✦</span>
                <p>
                    The localization word. It is used to locate (and open)
                    someone else's Gate. Doing so, however, does not give the
                    caster any information about this person's current state or
                    whereabouts. There is no way to know anything about someone
                    just by opening their Gate. Also, one will not be able to
                    open a gate with someone he never met before.
                </p>
            </div>
            <div className="tome-spell-entry">
                <span className="tome-spell-word">✦ Preldian ✦</span>
                <p>
                    The transportation word. Once a gate is opened between two
                    beings, this word will allow instant transportation between
                    them. It is up to the caster to decide which of the two will
                    come to the other.
                </p>
            </div>
        </div>

        <p>
            Those three first words pronounced together form the most basic and
            easy to use Black Magic spell:{" "}
            <span className="tome-spell-inline">✦ Mora Olsa Preldian ✦</span>
        </p>

        <div className="tome-spell-list">
            <div className="tome-spell-entry">
                <span className="tome-spell-word">✦ Elarum ✦</span>
                <p>
                    The conjuration word. This word will open a very special
                    Gate, not bound to any being or place, but out of space and
                    time. It is very hard to describe the purpose of such a
                    Gate, as there are no common words to describe the place it
                    opens to. This is a preparation spell, as it will give the
                    magician the place and energy to create something from
                    nothing, before bringing it into our world.
                </p>
            </div>
            <div className="tome-spell-entry">
                <span className="tome-spell-word">✦ Erarum ✦</span>
                <p>
                    This word is very similar to the conjuration word, but it
                    serves the opposite purpose. The Gate this word will open
                    leads to absolutely nothing, and anything crossing it will
                    instantly vanish into eternity... Using the words Elarum and
                    Erarum in the same spell, as pointless as it may seem, is
                    actually quite often used, as it can allow the magician to
                    be granted more Black Magic energy. Elarum will open a Gate
                    and create this energy, which will flow through the
                    magician's body, who will then use it as he sees fit.
                    However, since the magician has to be very careful the
                    energy does not take control over his body, he will also
                    open a Gate to nothing, where the unused energy will vanish.
                    The sentence{" "}
                    <span className="tome-spell-inline">
                        ✦ Elarum Mora Erarum ✦
                    </span>{" "}
                    typically gives the magician a temporary huge energy boost,
                    which he can use to then cast a major "spell".
                </p>
            </div>
            <div className="tome-spell-entry">
                <span className="tome-spell-word">✦ N'eshra ✦</span>
                <p>
                    The summoning word. This word will be used to create enough
                    lifeforce to inhabit a "lesser" body. By lesser body I am
                    talking about what we humans usually refer to as Monsters.
                    Fortunately, the amount of lifeforce needed to create a
                    human is far too great for any known magician to handle.
                    Some have tried, no one actually survived.
                </p>
            </div>
            <div className="tome-spell-entry">
                <span className="tome-spell-word">✦ Alt'er ✦</span>
                <p>
                    The alteration word. This word is surprisingly close to our
                    common word "alter", and it serves the whole purpose. After
                    opening a Gate to another human being, this word is used to
                    alter some of its functions. A weak magician could prevent
                    someone from talking or moving, while a powerful one could
                    literally take control of someone else...
                </p>
            </div>
            <div className="tome-spell-entry">
                <span className="tome-spell-word">✦ Phesta'h ✦</span>
                <p>
                    This word is the last one I could define in my years of
                    research, and I am not quite sure about its function. All
                    that I know is that this word was used in two events that
                    looked quite similar to me. The first one was during a
                    battle. A human leader decided it was time to teach a tribe
                    of lizardmen a lesson, and sent a ranger to scout the camp.
                    As soon as the ranger left, the leader went to his room and
                    asked not to be disturbed whatever the reason might be. It
                    is said that a curious servant listened to the door and
                    heard the human leader casting the{" "}
                    <span className="tome-spell-inline">
                        ✦ Mora Olsa Phesta'h ✦
                    </span>{" "}
                    spell. A few hours after the Ranger left, the leader came
                    out of his room and claimed it was time for them to attack.
                    Surprisingly, this day, not a single human fell into the
                    many traps the lizards set...
                </p>
                <p>
                    When I was only an apprentice, my master woke me up one
                    morning and told me that a thief managed to steal one of his
                    parchments while he was asleep, and that he saw him running
                    into the mountains. I was still very sleepy and could hardly
                    follow my hasty master, but after casting the very same
                    spell, he was able to tell exactly where the thief was
                    hiding. I will not make any conclusion about what this word
                    could be used for.
                </p>
            </div>
        </div>

        <p className="tome-closing">
            Here ends the first tome of my Black Magic encyclopedia. After
            reading this, you should be able to apprehend and maybe even
            understand what took my master and myself years and years to figure
            out, and what will be on the other tomes I will write after this
            one.
        </p>
    </>
);

export const BlackMagicTomeIIContent: React.FC = () => (
    <>
        <p className="tome-attribution">
            <em>by Neth, Black Magician</em>
        </p>

        <p>
            It took me some time to finally start writing this second Tome, but
            I am glad I did. Over the few months that passed since I finished
            writing the first one, I saw more and more consumed roaming the
            lands, old fools who thought they could be stronger than what
            created the world itself.
        </p>
        <p>
            Now is the perfect time for me to write a Tome about the
            side-effects of manipulating Black Magic, this vicious art. As
            previously mentioned, opening your own Gate and letting Black Magic
            flow into it is not without consequences. This Magic will taint your
            own soul, darkening your body, pushing it to the very last states of
            decomposition, and, eventually, kill you...
        </p>
        <p>
            As mentioned before, the first signs of an overuse of Black Magic is
            dark spots appearing on the Artist's hands. The spots will grow
            bigger with time, and there is no way to get rid of them. I wrote
            the first Tome with many little spots covering my right hand. At
            that time, I thought it was funny, a sign of power. I was dreaming
            of the day when I would wake up with black hands and could tell my
            master "Master! Look! I am powerful now!". When this day arrived, my
            Master showed me how much of a fool I was...
        </p>
        <p>
            What one has to understand is that the use of this powerful magic
            corrupts your perception. There is no feeling greater than this
            sensation of superiority that takes over your mind when you let
            Black Magic flow inside your body, that feeling you could dominate
            the whole world just by letting a little bit more of it inside you,
            just a little bit... Shall one fail to resist, their Death is
            guaranteed.
        </p>
        <p>
            The dark spots, of course, are only the beginning. As time goes by
            this Darkness invades all your body, slowly making its way to your
            heart, your mind, your soul. When this happens, you are no longer a
            Black Magician. You are no longer an Artist. You belong to Black
            Magic. Your body starts decaying just as if it was dead, and the
            darkest thoughts invade your soul. Some beings are more resistant
            than others, but they all fail, eventually. They all do.
        </p>
        <p>
            As your body slowly turns into one of a Lich, your corrupted soul
            loses all its power. I saw dead Black Magicians wandering inside a
            temple, crying to themselves why, oh why were they still dead,
            slowly realizing it was way too late for their soul, slowly
            realizing they already belonged to that power they once tried to
            manipulate.
        </p>
        <p className="tome-handwriting-note">
            <em>
                The end of the Tome is written with a much more insecure
                writing. It is obvious it was made with a shaking, trembling
                hand.
            </em>
        </p>
        <p>
            Today, my Master has fallen. He became a consumed and attacked me.
            He said he needed my body to survive, he said he found a way to
            transfer his soul into it. He told me that if I loved my master I
            should give him my body. What a fool. When one of my lightning bolts
            torn his robe apart, I could see with my own eyes what too much
            Black Magic brings. The ultimate Darkness. Dark rays coming out of
            his chest, spreading through his whole body like veins, reaching his
            black hands, which he could barely move. With every lightning bolt a
            bit of his own hand's skin would fall to the ground, his guttural
            voice claiming the whole world would be his. The whole world.
        </p>
        <p>
            I hope one day this Tome will make it into a great Library. The
            world has to know. This Magic has to be forbidden. It has to be
            forgotten.
        </p>
        <p className="tome-handwriting-note">
            <em>
                Here the writing becomes even more uncertain, barely readable.
            </em>
        </p>
        <p>
            Actually, I hope it doesn't. I was a fool when I wrote this Tome. I
            should even burn it. Black Magic is so... Powerful. It can bring so
            much... There is nothing that cannot be done with it. One just has
            to know how to resist. Darkness? I don't care. Those black veins
            look much better on my chest than on everyone else's. I, unlike my
            master, learnt how to resist. I learnt how to use this energy. It is
            mine, I can do whatever I want. I will take on the worlds as I see
            fit, and no one will be able to stop me. NO ONE.
        </p>
        <p className="tome-handwriting-note">
            <em>*The last page was ripped off*</em>
        </p>

        <div className="tome-divider">
            <span>✦ ✦ ✦</span>
        </div>

        <h3 className="tome-chapter-heading">
            Black Magic: Tome II — The Missing Pages
        </h3>
        <p className="tome-attribution">
            <em>As deciphered by Cecil</em>
        </p>
        <p>
            It is really strange to see how the two first tomes were written in
            our language, but everything ripped from the latter. I still cannot
            figure out why it was written in reversed ancient language. The fact
            is, using the golden mirror my old grandfather gave me when I was a
            child — which his own Grandfather gave him, he said — and my
            knowledge in ancient languages, I could decipher it. The Ancient
            Language contains words that describe feelings much better than our
            own, and I am afraid some part of it I could not translate so well.
        </p>

        <div className="tome-diary">
            <div className="tome-diary-entry">
                <span className="tome-diary-date">
                    Mirtius, 6th, Yellow Moon
                </span>
                <p>
                    It is incredible how fast my researches are going. Black
                    Magic is keeping me awake although I don't eat or sleep
                    anymore. I have understood this Ancient language in less
                    than a month, and am now fully ready to move on to the next
                    step. I am a Black Artist, I will become a Black Artisan. I
                    will create my own spell. Open new Gates now no one ever
                    opened before. All I need is to find that old Tome...
                </p>
                <p>
                    Tonight, I will become one with the Magic. I will destroy
                    all my barriers. I will let Black Magic not only flow into
                    me, but possess me. I will become Black Magic. I will become
                    the world. I will be every single tree, every single rock,
                    every single lifeforce. I will be the world. I will be
                    better than GoD himself...
                </p>
            </div>
            <div className="tome-diary-entry">
                <span className="tome-diary-date">
                    Mirtius, 7th, Yellow Moon
                </span>
                <p>
                    Hahahahaha! I did it! I know where that Tome is! I will send
                    my minions to retrieve it. Soon I will be stronger than
                    every Black Artist ever was! The worlds will be mine so
                    easily it will not even be funny. I will have to create a
                    spell that will keep me entertained for ages... I already
                    thought about some things....
                </p>
            </div>
            <div className="tome-diary-entry">
                <span className="tome-diary-date">
                    Jarius, 16th, Brown Moon
                </span>
                <p>
                    I have finished reading the Tome today. A bunch of peasants
                    came to my camp trying to retrieve it, pathetic humans. They
                    have no idea of the power I wield. I used Black Magic to
                    break their legs, and now they are all standing on the
                    ground. I wonder what will kill them first. Starvation?
                    Rats? Heat?
                </p>
            </div>
            <div className="tome-diary-entry">
                <span className="tome-diary-date">
                    Jarius, 18th, Brown Moon
                </span>
                <p>
                    How funny. I went to see the peasants today and noticed a
                    wonderful thing. One of them had a gourd full of water, and
                    took it out of his bag to ease his thirst. No sooner had him
                    done that, another peasant crawled to him and actually
                    killed him with his axe to steal some water! It was so
                    amazingly fun to watch I think I decided what my spell will
                    be...
                </p>
            </div>
            <div className="tome-diary-entry">
                <span className="tome-diary-date">
                    Jarius, 25th, Brown Moon
                </span>
                <p>
                    Took me a while. Silly wisps. They keep disturbing me while
                    I am thinking. The peasants are all dead. Most of them
                    killed each other. I just love it. I am now ready to create
                    my new Black Magic spell. Since I definitely am the
                    Strongest Black Artisan of all the worlds, I decided my
                    spell would wear my own name. I called it "The Neth." It
                    will be a real plague. Once I cast it, everyone who ever
                    used Black Magic once in their life will become a
                    bloodthirsty human being. A serial murderer. They will only
                    live to kill other humans, to steal their possessions, to
                    feast in their blood. Each world I will conquer will see its
                    own inhabitants killing one another, and the poor little
                    innocent people who never used Black Magic will see their
                    friends and teachers killing one another, and killing them
                    too. They will have to fight back too... Even without the
                    Neth, they will have to kill their own friends... Ohh, my, I
                    just love it. I just love myself.
                </p>
            </div>
            <div className="tome-diary-entry">
                <span className="tome-diary-date">
                    Jarius, 27th, Brown Moon
                </span>
                <p>
                    I talked to Black Magic today. I know the reagents needed to
                    create my spell. I will start gathering them.
                </p>
            </div>
            <div className="tome-diary-entry">
                <span className="tome-diary-date">Quiriat, 4th, Red Moon</span>
                <p>
                    Stupid book. I wonder why I am still writing on it. I lost
                    an arm today. I don't care. The destruction of worlds can
                    cost as little as an arm. I need three more reagents. I need
                    three more reagents. I need three more reagents. I need
                    three more reagents. I need three more reagents. I need
                    three more reagents. I need three more reagents. I need
                    three more reagents. I need her too. I do.
                </p>
            </div>
            <div className="tome-diary-entry">
                <span className="tome-diary-date">
                    Agrabal, 15th, Blood Moon
                </span>
                <p>
                    "One more. Need one more. Must have it. Located in a chest
                    in the center of this world. Got her too. Saw her. Killed
                    her. The Neth is almost ready. The worlds are mine."
                </p>
            </div>
        </div>

        <p className="tome-handwriting-note">
            <em>
                These were the last words from the old fool called Neth. The
                rest of the pages were written in our language by someone else,
                whose name I could not find anywhere.
            </em>
        </p>

        <blockquote className="tome-blockquote">
            "We did it. We defeated Neth. Without the help of that red robed old
            wizard, we would never have managed to do it. I was right to trust
            him. I am glad I did."
        </blockquote>

        <p>
            The old wizard sent Neth a vision, telling him to seek the last
            reagent in a dungeon we knew as empty. We put an old chest here, and
            the old wizard dropped a very special item in it. I could not say
            what it was, but I remember, when the old wizard took it out of his
            pouch, our village's leader, the old mage Salazer, stood in shock,
            his eyes started to cry, and he said "Curio...". Today, he cannot
            remember he did it, but we were all here, and we saw it.
        </p>
        <p>
            The red robed wizard then cast a Black Magic spell on the chest, and
            we all hid when Neth arrived. He was but a mere Lich. One of his
            arms had already fallen off from decomposition and the rest of his
            body looked like it could do the same at any moment. His whole skin
            was dark, just as if he walked through fire, and somehow survived
            from it.
        </p>
        <p>
            Neth opened the chest, and the "Curio" started glowing a wonderful
            blue light, one that took all of us in a trance, unable to do
            anything but staring at it, while Neth was holding it in his own
            hands, babbling something in an incomprehensible language. Neth sat
            to the ground — or, should I say, Neth's body fell to the ground —
            and he put two other artifacts looking exactly the same next to the
            "Curio". The first one was all red, while the green light flowing
            from the third looked even more appeasing than the others. We were
            all smiling, kneeling at Neth.
        </p>
        <p>
            At that point, we all thought that wizard had betrayed us, but when
            Neth put the three pieces of the artifact together, a moon
            crescent-shaped blinking white light invaded the dungeon's room. We
            all became deaf, or maybe the light was just too astonishing for us
            to be able to hear anything. The ground underneath us disappeared.
            For a moment — how long I could not tell — we were all floating,
            watching the white light piercing through Neth's body, consuming the
            very last lifeforce he had in him, trapping it in the chest the red
            robed wizard prepared.
        </p>
        <p>
            When Neth and the light totally disappeared, only the strongest of
            us could stand on their feet. The chest was sealed, by a Magic I
            could never understand, and our village's leader spoke, with a voice
            that was not his, but the red robed wizard's. He said, "The Neth is
            trapped inside the chest. Whoever will try to open it will die.
            Shall someone be powerful enough to break the seal, the Neth will
            wake up. Everyone who already used Black Magic will become the exact
            opposite of what they used to be. That is the only thing Neth was
            able to do before he was trapped. Protect this chest, it is worth
            more than a thousand of your lives." I fully intend to follow that
            advice. Even with that light consuming him, Neth was able to create
            a spell. Not exactly what he wanted to create, but powerful enough
            to destroy this world. The chest cannot be destroyed, and I don't
            think it ever will, but as long as I live, as long as my children
            live, as long as the children of my children live, it will stay
            closed.
        </p>

        <p className="tome-handwriting-note">
            <em>
                Obviously, the last of these pages was written years after the
                event.
            </em>
        </p>

        <p>
            "So far from swearing with my own life. Gidéon opened the chest, he
            unleashed the Neth. The predicted disaster happened. Everything
            happened just as that red robed wizard told us. Everyone got struck
            by the Neth. I myself... When I woke up and saw what I did... My own
            sister... My own mother... The King and Steward of Mirith killed
            Gidéon and managed to close the chest again. The Neth is sleeping
            once more now... Until when... I am fed up with this. I cannot look
            at myself anymore. I killed my family.... I will have no children.
            The chest will be forgotten, for the sake of humanity. I do not want
            to be there when it is opened again. I am just going to let myself
            die, just like Neth let the peasants dying. I will just sit here,
            and die."
        </p>
        <p className="tome-closing-italic">
            <em>I am sorry...</em>
        </p>

        <div className="tome-divider">
            <span>✦ ✦ ✦</span>
        </div>

        <p className="tome-attribution">
            <em>— Cecil</em>
        </p>
        <p>
            The time-stamps used by Neth in the notes are definitely not Ancient
            Language. I really have no idea what they are about. I think it is
            something Neth probably made up; seeing how insane he was at the end
            of the pages, it would not surprise me. It was a terrifying book,
            and I am really glad I am done with it. I had never heard of the
            Neth before. I cannot put myself to believe this actually happened.
            I have no idea if the chest still exists, if it ever existed to
            begin with, and if it does, whereabouts could it be — but I think I
            would just kill myself if I did...
        </p>
    </>
);

export const OnMagicUsersContent: React.FC = () => (
    <>
        <p className="tome-attribution">
            <em>by Anonymous; translated by Birek McCalla</em>
        </p>

        <blockquote className="tome-blockquote">
            "Magic saturates Oberin, magic permeates every rock of Oberin, every
            tree, every animal, every human.
        </blockquote>
        <blockquote className="tome-blockquote">
            Some beings let this magic wash over them unnoticed — the common
            beasts. To them magic is unknown. The lightning bolt will kill them,
            but they are as unaware of how it came to be as they are of how a
            sword comes from the mountains that line their horizons.
        </blockquote>
        <blockquote className="tome-blockquote">
            Some understand the uses of magic, creatures of intelligence. Oberin
            is home to two such creatures: Dragons and humans. Both are capable
            of utilising magics in similar ways, though the frequency between
            them varies greatly. Study of dragons is a hard task, and all that
            can be said with certainty is few dragons exist, and very few are
            known to use magic. Where they do, it is always very powerful.
            Humans on the other hand are far more populous and many use magic to
            some degree.
        </blockquote>
        <blockquote className="tome-blockquote">
            The third class is creations of magic. Inanimate objects brought to
            existence by magic. The undead being by far the most commonly seen
            example. These can further be divided into those which are the
            product of another's magic, and those maintained through their own.
            The mindless skeleton as compared to the lich. Whether these should
            count as a class of creature or a use of magic is debatable.
        </blockquote>
        <blockquote className="tome-blockquote">
            The final class is the creature of magic — not merely using magic,
            not merely maintained through magic, but born of living magic. Of
            these only one exists, and one is theorised. Some powerful Black
            Magicians have dedicated their lives to freeing themselves of flesh
            and becoming beings not bound to the physical realm of Oberin, to
            become as a God. To my knowledge none has succeeded.
        </blockquote>
        <blockquote className="tome-blockquote">
            The other creature of magic is the Gaper. Gapers are not the
            intangible essences the Magicians wish to become. They have physical
            bodies in Oberin, bodies which can be destroyed. However, they
            appear to have no means of sustenance beyond magic — a gaper has no
            mouth, and so it does not eat. The flesh is sustained by some
            process of absorption of the natural magic of Oberin."
        </blockquote>
    </>
);

export const HistoricalMythologyTomeIContent: React.FC = () => (
    <>
        <h3 className="tome-chapter-heading">The Unbalancing</h3>
        <p className="tome-attribution">
            <em>Written by Cecil, lead historian and clergyman at Brigobaen</em>
        </p>

        <blockquote className="tome-blockquote">
            "There was nothing in the beginning, nothing but GoD. A vast waste
            of nothingness, an empty void, devoid of all life and energy. There
            was nothing but GoD."
        </blockquote>
        <blockquote className="tome-blockquote">
            "GoD peered out into the nothingness; the window overlooking the
            field of night. Perpetual night. Darkness and gloom, darkness and
            gloom."
        </blockquote>
        <blockquote className="tome-blockquote">
            "GoD saw the imbalance of nothing. A door leading to nowhere, a
            window viewing a blank slate, the darkness pervading everything. No
            life, no energy, no spark."
        </blockquote>
        <blockquote className="tome-blockquote">
            "GoD sought to correct the balance, with a divine spark of thought,
            He created the World, the World of Oberin. The window would overlook
            a field of green, the door would open to the light of day. Lightness
            and air, lightness and air."
        </blockquote>
        <blockquote className="tome-blockquote">
            "The World was created in an instant, a flash of Thought. Trees,
            rocks, water, air, all appeared at once. Thrown together. A harmony,
            sung sweetly, pervaded what was empty and desolate, empty and
            desolate."
        </blockquote>

        <p className="tome-source-note">
            From "The Shurilizuk, Tome of the Old. Verses one through five."
        </p>

        <p>
            Notice a few elements from these opening lines: <strong>1.</strong>{" "}
            The use of GoD, instead of God. The meaning of this convention is
            unknown; later the name will change and new conventions will be
            used. <strong>2.</strong> The use of illustrative metaphor; here,
            windows and doors are used. These are used often in the Shurilizuk.
            Others are used later. <strong>3.</strong> The theme of balance is
            of utmost importance. Keep these in mind when reading the
            Shurilizuk.
        </p>
        <p>
            From here, The Shurilizuk gets a bit spotty. The only copy we have
            of the ancient text has endured a lot of damage. Much of the Tome of
            the Old has been lost. With great difficulty, priests have attempted
            to translate what was left and fill in some of the gaps. Here are a
            few verses that need to be mentioned, in regard to the Unbalancing.
        </p>

        <blockquote className="tome-blockquote tome-damaged">
            "... (Man) however, sought dominance over (the balance), Man was
            ignorant, he sought to (empower?) himself over others, destroy the
            balance."
        </blockquote>
        <blockquote className="tome-blockquote tome-damaged">
            "The house was in disarray, the window looked over a sea of Black,
            the door had been fixed (closed?), the World was fading into a sea
            of Black, sea of Black, darkness would engulf us All."
        </blockquote>
        <blockquote className="tome-blockquote tome-damaged">
            "Man held a Black power, dominance over the energy, the energy that
            flowed through (...), (Man wielded the power) for his own gain, Man
            wielded the Black to dominate All."
        </blockquote>
        <blockquote className="tome-blockquote tome-damaged">
            "The Black unleashed, washed over the World, awakening our GoD, who
            surveyed His World turning Black, (His) window (was engulfed) in
            dark(ness?)."
        </blockquote>
        <blockquote className="tome-blockquote tome-damaged">
            "GoD understood what had occurred, allowing Man to reach (too
            high?), to disturb the fragile balance, to disturb the (shallow)
            harmony, the Song of the World, the harmony had to be (restored)."
        </blockquote>
        <blockquote className="tome-blockquote tome-damaged">
            "God punished Man, allowed the Black to (flow over them and destroy
            them?), forcing them to flee into the bowels of (the World), where
            they would remain. Balance would be (restored) by the flow of Black,
            (tenuous balance until Man learned)."
        </blockquote>

        <p className="tome-source-note">
            From "The Shurilizuk, the last 6 verses of the Tome of the Old."
        </p>

        <p>
            Here, we see that Man, at some point in the distant past, attempted
            to disturb the balance of the World, the balance created by GoD. We
            think it had something to do with a "Black Power," which may have
            had to do with Black Magic (see the Tome on Black Magic written by
            Neth). Man was driven from the world by the Black, whatever physical
            manifestation it took, and were driven into hiding, where they must
            have stayed for many years.
        </p>
        <p className="tome-closing">
            So ends the creation and first Unbalancing of Oberin.
        </p>
    </>
);

export const HistoricalMythologyTomeIIContent: React.FC = () => (
    <>
        <h3 className="tome-chapter-heading">Haven</h3>
        <p className="tome-attribution">
            <em>
                An excerpt from "The Haven"; an essay written by Cecil of
                Brigobaen
            </em>
        </p>

        <blockquote className="tome-blockquote">
            "The Black pursued Man, sending him speeding from the hospitable
            places and driving him down, down, into the belly of the World."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Desperate Man, desperate to live on, desperate to see the green
            fields through the window, desperate to walk through the (welcome)
            door, deep in the cave, sought Haven."
        </blockquote>

        <p className="tome-handwriting-note">
            <em>(several verses are missing here)</em>
        </p>

        <blockquote className="tome-blockquote tome-damaged">
            "Haven was not available to Man, his transgressions too great. Man,
            in his arrogance, created a haven for himself in the bones of the
            World. A flash of the last power, deep in the bones, created a
            pocket haven for Man to exist, exist away from the Black."
        </blockquote>
        <blockquote className="tome-blockquote tome-damaged">
            "In the creation of the Haven, Man angered GoD further, truly not
            learning the lessons of balance. In the unleashing of the last
            power, the very heavens shook, the balance of the Sun fell, crashed
            from overhead."
        </blockquote>
        <blockquote className="tome-blockquote tome-damaged">
            "A blast that could be heard for hours, a blast that echoed through
            the haven of Man and the bones of the World, shook the window on the
            World and fixed the door closed for (forever?)."
        </blockquote>
        <blockquote className="tome-blockquote tome-damaged">
            "Man would not know his transgression, the erasure of the Sun
            balance, Man forever sealed away from the sunlit World, in an
            unknown vault of forgettable treasure, Forever sealed in Haven."
        </blockquote>

        <p className="tome-handwriting-note">
            <em>(a few verses are missing here)</em>
        </p>

        <blockquote className="tome-blockquote">
            "Man existed in haven for the passage of many sunlit days, kept
            alive by the warmth of the bones, newly warmed by the unleashing of
            the last power, kept alive by the last flicker of Black Power Man
            only sought to exist."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Man hoped that God would forgive, restore the balance and the World
            to its former glory, a glory that Man erased for his intolerance of
            natural order."
        </blockquote>
        <blockquote className="tome-blockquote">
            "God, it seems, would forgive..."
        </blockquote>

        <p className="tome-source-note">
            From "The Shurilizuk, Tome of Haven."
        </p>

        <p>There are several puzzling terms used in the Tome of Haven.</p>
        <p>
            <strong>1.</strong> Haven, when first written about, is capitalized.
            Was there a place of safety in the world that was known to people?
            Those missing verses would be helpful to understand this odd
            convention.
        </p>
        <p>
            <strong>2.</strong> The middle verses seem to indicate that Man was
            responsible for the creation of the Volcano as it is now. This
            incredible surge in Oberin's tectonics would have fundamentally
            changed the landscape (as you can see in the next Tome). How Man
            came to wield such power is unknown.
        </p>
        <p>
            <strong>3.</strong> The balance of the Sun has been argued about for
            many years by historians and priests. There is much scholarly debate
            on this topic. I would argue that the balance of the Sun is actually
            the physical Moon. My reasoning for this will be apparent in the
            next essay; however, there are other interpretations.
        </p>
        <p>
            <strong>4.</strong> At some point, during the writing of this Tome,
            the capitalized D of GoD was dropped, and the name "God" is used. It
            is unclear as to why this naming convention changed. It may have
            been changed later, as revisions and edits were done.
        </p>
    </>
);

export const HistoricalMythologyTomeIIIContent: React.FC = () => (
    <>
        <h3 className="tome-chapter-heading">Emergence from Haven</h3>
        <p className="tome-attribution">
            <em>
                An excerpt from an essay written by Cecil, priest of Brigobaen
            </em>
        </p>

        <p>
            The next Tome of the Shurilizuk, called <em>Hymns of Solitude</em>,
            has a different feeling to it than the previous two. One gets the
            sense that it was written for a different audience, perhaps even for
            a different purpose altogether.
        </p>

        <blockquote className="tome-blockquote">
            "Man waited in the caves of flame, hoping that their penance would
            erase their shame. Man was eager to shoulder the blame. Hail God!
            May his glory never wane."
        </blockquote>
        <blockquote className="tome-blockquote">
            "God is glory, sent from on high, may one day Man see the sky. Man
            stirs in the cave to cry, Hail God! Tears of joy for being alive."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Many years pass, waiting in the cave. Man cursing himself, the
            foolish knave. Man learns joy for what He gave, Hail God! May his
            Mercy the way pave."
        </blockquote>

        <p className="tome-source-note">
            From The Shurilizuk, Hymns of Solitude, Numbers 11, 12, and 17.
        </p>

        <p>
            The hymns continue for many pages. The rhyme scheme is clumsy at
            best, and sometimes the language is somewhat confusing. In spite of
            all this, these hymns must have served a purpose for Man during his
            solitude. I believe they served to lift the spirits of the condemned
            and show God that his children wanted to relearn what had been
            forgotten.
        </p>
        <p>
            I will skip the introduction of the next Tome,{" "}
            <em>The Emergence</em>, and get to its critical parts. The most
            critical part of The Emergence is the entrance of Elara, the first
            human named in The Shurilizuk (that we can tell).
        </p>

        <blockquote className="tome-blockquote">
            "Among them was Elara; mage of the stars, wisest of all Man,
            timeless Elara the mage."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Eldest Elara, in all her years, divined the purpose of God,
            relearned the Balance of All, and sought to return to the World."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Humble Elara sought to show, that Balance would be preserved."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Wise Elara took from her daughter's arms a newborn babe of her
            image, sought the secrets of Haven, to find the outlet to the Sky."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Courageous Elara wandered the Haven, allowing the light of God to
            fill a new Window upon the World, an old Door to swing upon the
            grasses again."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Master Elara chanted the hymns of joy and sorrow to God, songs of
            Balance and Mercy, dutiful words to the God on high."
        </blockquote>
        <blockquote className="tome-blockquote">
            "The newborn wept, seeking the food of the World. Mother Elara wept,
            seeking the food of God."
        </blockquote>
        <blockquote className="tome-blockquote">
            "God saw the weeping, and gnashing of teeth, and the hunger for His
            food, and for his Mercy and Love."
        </blockquote>
        <blockquote className="tome-blockquote">
            "God's light fell upon a dark space in Haven, a new pathway seen yet
            unseen by Man, cold yet warm, light yet dark, wet yet dry, the
            outlet to the Sky."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Lighted Elara emerged from Haven, on top of the Fount of Flames,
            breathing in the sweet smells of freedom."
        </blockquote>
        <blockquote className="tome-blockquote">
            "God had forgiven Man."
        </blockquote>

        <p className="tome-source-note">
            From The Shurilizuk, Tome of Emergence, verses 21–32.
        </p>

        <p>
            Pretty powerful stuff. It continues for several verses about the
            lands spread out in front of her, and so on. It gets more
            interesting many verses later, as we will see.
        </p>

        <blockquote className="tome-blockquote">
            "Darkness fell around Elara, pitch black nothingness, drawing around
            her like a cloak, a cloak of pitch-black nothing."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Determined Elara cast her eyes about, looking for light to alight
            her way, she peered into the mouth of the Fount, and saw the
            brightness catch flight."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Ice-blue stone, smooth as glass, jutting out of the fiery heat,
            wisps of ice-blue smoke."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Astonished Elara heard a voice, a voice of Old."
        </blockquote>
        <blockquote className="tome-blockquote">
            "Here lies the Moon, Moon cast down by Man, dissolving away for
            eternity. What has Man learned?"
        </blockquote>
        <blockquote className="tome-blockquote">
            "Mage Elara raised her companion overhead, proclaiming the Balance
            had been created again, decrying the Sins of Man and glorifying
            God."
        </blockquote>

        <p className="tome-handwriting-note">
            <em>(several verses are missing)</em>
        </p>

        <blockquote className="tome-blockquote">
            "If I must sacrifice this child, to prove my worth, I will do it.
            God is the World and the Balance is God."
        </blockquote>
        <blockquote className="tome-blockquote">
            "God was pleased. Man had learned."
        </blockquote>

        <p className="tome-source-note">
            From The Shurilizuk, Tome of Emergence, verses 41–46, 52–53.
        </p>

        <p>
            The tome is mostly incomplete from here. We can piece together
            (mostly from later Tomes and associated texts) that Elara somehow
            dislodged the stone from the fiery Fount. She deduced that it had
            some kind of power that she could bend to her will. Her fellow Man
            did not believe her, so she used the stone to banish the "Black"
            (there's that word again) into the bowels of the bones of the World.
            Man emerged from the Fount and settled anew.
        </p>
    </>
);

export const HistoricalMythologyTomeIVContent: React.FC = () => (
    <>
        <h3 className="tome-chapter-heading">The New Order</h3>
        <p className="tome-attribution">
            <em>
                An excerpt from an essay written by Cecil, head Cleric of
                Brigobaen
            </em>
        </p>

        <p>
            The next section of The Shurilizuk is truly another amazing turn of
            prose. The previous Tomes used a kind of poetry, whereas this next
            selection uses an almost story-like format. Let us take a look,
            shall we?
        </p>

        <blockquote className="tome-blockquote tome-numbered">
            <span className="tome-section-number">I.</span> And Elara's triumph
            over the Black was total, and the people rejoiced, and there was
            much praise and glory given to all around. And Elara smiled and
            wept, and the people smiled and wept. (...) followed by a great
            feast, loaves of bread and steaks of fish and sheep were shared, and
            the people cheered, the bounty of the World was plentiful.
        </blockquote>
        <blockquote className="tome-blockquote tome-numbered">
            <span className="tome-section-number">II.</span> The people needed a
            place to settle. And GoD said unto Elara, "Settle in the fertile
            plains here, may your progeny spread through the World again." And
            Elara, following the divine shining beam, led her people to the
            plains. There, in a valley between the mountains and a bountiful
            river, a settlement would be created.
        </blockquote>
        <blockquote className="tome-blockquote tome-numbered">
            <span className="tome-section-number">III.</span> Her people said
            unto Elara, "We wish for you to lead us, Wise Elara." Elara wept,
            for her people had suffered long, and she wished their suffering to
            end and a golden age to begin. Elara declared to her people, "I will
            do what you ask. Who among you will learn at my feet and aid in my
            duty? For to lead Man is a job that I am capable, but disciples I
            will need for my time is short."
        </blockquote>
        <blockquote className="tome-blockquote tome-numbered">
            <span className="tome-section-number">IV.</span> Her people could
            not believe that Elara was not long for this world. Elara was there
            before even the eldest of Man living now; there was none before her.
            Elara was timeless and eternal. Elara spoke to the masses gathered
            before her, "All shall shuffle off this mortal coil, as is the Will
            of GoD."
        </blockquote>
        <blockquote className="tome-blockquote tome-numbered">
            <span className="tome-section-number">V.</span> Gathered to Elara
            were the Disciples of Elara — 7 youth, skilled in the martial and
            magic arts. These would be the students to Elara's mastery, the
            Disciples would learn at the feet of Elara the Brave. The Disciples
            would aid in administering the will of Elara, maintaining the
            Balance that Man had once Sundered.
        </blockquote>

        <p className="tome-source-note">
            From The Shurilizuk, Tome of Elara, Sections 1 through 5.
        </p>

        <p>
            The 7 Disciples, led by Elara, headed a kind of "central government"
            of Man in Oberin. The place that is described in Section II has been
            debated by many historians and theologians over the years — but it
            seems obvious to this cleric that they are talking about Mirith. The
            Tome of Elara does not make too much out of the timeline of
            foundings, but it is something that the Mirith Royalty take very
            seriously. In the next sections, we will learn more about what
            appears to be a relatively calm period of Oberin history.
        </p>

        <blockquote className="tome-blockquote tome-numbered">
            <span className="tome-section-number">IX.</span> And the city of
            Mirith was founded by Elara, a city of fame and glory, steadfast and
            prosperous, home of traditions and the birth of culture. And the
            city of Andris was founded by Nystral, the First Disciple, a city
            based on logic and reason, gleaming marble and scholarly debates,
            and the fount of knowledge.
        </blockquote>
        <blockquote className="tome-blockquote tome-numbered">
            <span className="tome-section-number">X.</span> The city of Lerilin,
            founded by (...) Disciple, seaside looking on the glory of the
            oceans of GoD, rippling waves and sunsets, the city of peace and
            serenity, and foremost neutrality. And Marali was founded last,
            founded by Ybarra, the third Disciple, the fort of order and
            regiments, the last bastion of Man in the otherwise unfriendly
            environs of the Wilderness, the Rock.
        </blockquote>
        <blockquote className="tome-blockquote tome-numbered">
            <span className="tome-section-number">XI.</span> The Disciples 7 and
            Sage Elara led Man into their golden age, where peace reigned,
            learning prospered and freedom rang from every corner of the World.
            Elara held the demons back, and the 7 learned her secrets and
            obtained the trials of the haven in order to rule in her stead. The
            Age was marked by Balance, and GoD was pleased.
        </blockquote>
        <blockquote className="tome-blockquote tome-numbered">
            <span className="tome-section-number">XII.</span> And the people
            rejoiced, and they paid reverence to Elara and the 7, and to GoD in
            sum total, for the (opening of the Door?) and for Holy Forgiveness.
            Man prospered in the light of the Shining Elara and her 7, may Elara
            never perish from Oberin and her light never fade.
        </blockquote>

        <p className="tome-source-note">
            From The Shurilizuk, Tome of Elara, Sections 9–12.
        </p>

        <p>
            The Golden Age... the Tome of Elara goes on for several more
            sections talking about it, but doesn't really give any insight into
            why it was given such an impressive title. More likely, the
            beginning of Man's reintroduction into Oberin was marked by strife
            and hardship. I believe the Tome of Elara was rewritten some time
            after this time period for whatever reason, most likely because of
            the problems that appear quite soon afterward.
        </p>
    </>
);
